<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">
              <i class="bi bi-people me-2"></i>
              Gestión de Clientes
            </h1>
            <p class="text-muted mb-0">
              Administra los clientes del sistema MyGeotab Feeder
            </p>
          </div>
          <router-link
            to="/clients/create"
            class="btn btn-primary"
          >
            <i class="bi bi-plus-lg me-1"></i>
            Nuevo Cliente
          </router-link>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="bi bi-funnel me-1"></i>
          Filtros
        </h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="applyFilters" class="row g-3">
          <div class="col-md-4">
            <label for="filterNombre" class="form-label">Nombre</label>
            <input
              id="filterNombre"
              v-model="localFilters.nombre"
              type="text"
              class="form-control"
              placeholder="Buscar por nombre..."
            >
          </div>
          <div class="col-md-4">
            <label for="filterCuit" class="form-label">CUIT</label>
            <input
              id="filterCuit"
              v-model="localFilters.cuit"
              type="text"
              class="form-control"
              placeholder="Buscar por CUIT..."
            >
          </div>
          <div class="col-md-4">
            <label for="filterActive" class="form-label">Estado</label>
            <select
              id="filterActive"
              v-model="localFilters.active"
              class="form-select"
            >
              <option :value="null">Todos los estados</option>
              <option :value="true">Activos</option>
              <option :value="false">Inactivos</option>
            </select>
          </div>
          <div class="col-12">
            <button
              type="submit"
              class="btn btn-primary me-2"
              :disabled="loading"
            >
              <i class="bi bi-search me-1"></i>
              Buscar
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="clearFilters"
            >
              <i class="bi bi-x-lg me-1"></i>
              Limpiar
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Clients Table -->
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          Lista de Clientes
          <span v-if="pagination.total" class="badge bg-secondary ms-2">
            {{ pagination.total }}
          </span>
        </h5>
        <div class="d-flex align-items-center gap-2">
          <select
            v-model="pageSize"
            class="form-select form-select-sm"
            style="width: auto"
            @change="changePageSize"
          >
            <option value="10">10 por página</option>
            <option value="20">20 por página</option>
            <option value="50">50 por página</option>
          </select>
          <button
            class="btn btn-outline-primary btn-sm"
            @click="refreshClients"
            :disabled="loading"
          >
            <i class="bi bi-arrow-clockwise" :class="{ 'rotate': loading }"></i>
          </button>
        </div>
      </div>

      <div class="card-body p-0">
        <!-- Loading State -->
        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando clientes...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="clients.length === 0" class="text-center p-5">
          <i class="bi bi-people fs-1 text-muted mb-3"></i>
          <h5>No hay clientes registrados</h5>
          <p class="text-muted mb-3">
            Comienza agregando el primer cliente al sistema
          </p>
          <router-link to="/clients/create" class="btn btn-primary">
            <i class="bi bi-plus-lg me-1"></i>
            Crear Primer Cliente
          </router-link>
        </div>

        <!-- Clients Table -->
        <div v-else class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>CUIT</th>
                <th>Dispositivos</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="client in clients" :key="client.id">
                <td>
                  <code class="text-primary">#{{ client.id }}</code>
                </td>
                <td>
                  <div class="fw-medium">{{ client.nombre }}</div>
                </td>
                <td>
                  <code>{{ client.cuit }}</code>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-info">
                      {{ client.device_count || 0 }} total
                    </span>
                    <span v-if="client.active_devices > 0" class="badge bg-success">
                      {{ client.active_devices }} activos
                    </span>
                  </div>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="client.active ? 'bg-success' : 'bg-secondary'"
                  >
                    <i class="bi" :class="client.active ? 'bi-check-circle' : 'bi-pause-circle'"></i>
                    {{ client.active ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>
                <td>
                  <small class="text-muted">
                    {{ formatDate(client.created_at) }}
                  </small>
                </td>
                <td>
                  <div class="btn-group" role="group">
                    <router-link
                      :to="`/clients/${client.id}`"
                      class="btn btn-outline-primary btn-sm"
                      title="Ver detalles"
                    >
                      <i class="bi bi-eye"></i>
                    </router-link>
                    <router-link
                      :to="`/clients/${client.id}/edit`"
                      class="btn btn-outline-secondary btn-sm"
                      title="Editar"
                    >
                      <i class="bi bi-pencil"></i>
                    </router-link>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      title="Eliminar"
                      @click="confirmDelete(client)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.pages > 1" class="card-footer">
        <nav>
          <ul class="pagination justify-content-center mb-0">
            <li class="page-item" :class="{ disabled: pagination.page === 1 }">
              <button
                class="page-link"
                @click="goToPage(pagination.page - 1)"
                :disabled="pagination.page === 1"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
            </li>

            <li
              v-for="page in visiblePages"
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.page }"
            >
              <button class="page-link" @click="goToPage(page)">
                {{ page }}
              </button>
            </li>

            <li class="page-item" :class="{ disabled: pagination.page === pagination.pages }">
              <button
                class="page-link"
                @click="goToPage(pagination.page + 1)"
                :disabled="pagination.page === pagination.pages"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>

        <div class="text-center mt-2">
          <small class="text-muted">
            Página {{ pagination.page }} de {{ pagination.pages }}
            ({{ pagination.total }} clientes total)
          </small>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="clientToDelete"
      class="modal show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.5)"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-exclamation-triangle text-warning me-2"></i>
              Confirmar Eliminación
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="clientToDelete = null"
            ></button>
          </div>
          <div class="modal-body">
            <p>
              ¿Estás seguro de que deseas eliminar el cliente
              <strong>{{ clientToDelete.nombre }}</strong>?
            </p>
            <p class="text-muted">
              <small>
                <i class="bi bi-info-circle me-1"></i>
                Esta acción desactivará el cliente pero no eliminará sus datos.
              </small>
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="clientToDelete = null"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteClient"
              :disabled="loading"
            >
              <i class="bi bi-trash me-1"></i>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useClientStore } from '../../stores/clients'

export default {
  name: 'ClientList',
  setup() {
    const clientStore = useClientStore()
    const localFilters = ref({
      nombre: '',
      cuit: '',
      active: null
    })
    const pageSize = ref(20)
    const clientToDelete = ref(null)

    // Computed properties
    const clients = computed(() => clientStore.clients)
    const loading = computed(() => clientStore.loading)
    const pagination = computed(() => clientStore.pagination)
    const totalClients = computed(() => clientStore.totalClients)
    const activeClients = computed(() => clientStore.activeClients)
    const inactiveClients = computed(() => clientStore.inactiveClients)

    const visiblePages = computed(() => {
      const current = pagination.value.page
      const total = pagination.value.pages
      const pages = []

      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)

      // Adjust if we're near the beginning or end
      if (current <= 3) {
        end = Math.min(total, 5)
      }
      if (current >= total - 2) {
        start = Math.max(1, total - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    })

    // Methods
    const applyFilters = async () => {
      clientStore.setFilters(localFilters.value)
      await clientStore.fetchClients(1, pageSize.value)
    }

    const clearFilters = async () => {
      localFilters.value = {
        nombre: '',
        cuit: '',
        active: null
      }
      clientStore.clearFilters()
      await clientStore.fetchClients(1, pageSize.value)
    }

    const goToPage = async (page) => {
      await clientStore.goToPage(page)
    }

    const changePageSize = async () => {
      await clientStore.changePageSize(pageSize.value)
    }

    const refreshClients = async () => {
      await clientStore.refreshClients()
    }

    const confirmDelete = (client) => {
      clientToDelete.value = client
    }

    const deleteClient = async () => {
      if (clientToDelete.value) {
        try {
          await clientStore.deleteClient(clientToDelete.value.id)
          clientToDelete.value = null
        } catch (error) {
          // Error is handled by the store
        }
      }
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Watchers
    watch(pageSize, (newSize) => {
      changePageSize()
    })

    // Lifecycle
    onMounted(() => {
      clientStore.fetchClients()
    })

    return {
      clients,
      loading,
      pagination,
      totalClients,
      activeClients,
      inactiveClients,
      localFilters,
      pageSize,
      clientToDelete,
      visiblePages,
      applyFilters,
      clearFilters,
      goToPage,
      changePageSize,
      refreshClients,
      confirmDelete,
      deleteClient,
      formatDate
    }
  }
}
</script>

<style scoped>
.rotate {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.modal.show {
  display: block !important;
}
</style>